import { useCurrentUserQuery } from "@base/model/hooks/useCurrentUserQuery";
import { useFullPageSheet } from "@base/ui/FullPageSheet";
import { Button, Chip, DatePicker, TextField } from "@buildblockinc/buildblock-design-system";
import type { DailyLogDetailResponse } from "@features/daily-log-detail/model/schema";
import { dailyLogDetailQueryOptions } from "@features/daily-log-detail/model/useDailyLogDetailQuery";
import { useProjectDetailQuery } from "@features/project-detail/model/useProjectDetailQuery";
import type { DailyLogForm } from "@features/write-daily-log/model/form";
import { useProjectWeatherQuery } from "@features/write-daily-log/model/useProjectWeatherQuery";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "@tanstack/react-router";
import dayjs from "dayjs";
import { Percent } from "lucide-react";
import { Suspense, useEffect, useRef } from "react";

import { DecimalNumberField, IntegerNumberField } from "./NumberField";
import { OverviewStatusSheet } from "./OverviewStatusSheet";
import { WeatherSelector } from "./WeatherSelector";

const MAX_PROCESS_RATE = 100;

function WriterField() {
  const { data } = useCurrentUserQuery();

  return (
    <TextField required readOnly>
      <TextField.Label>Writer</TextField.Label>
      <TextField.Input value={data?.user.name ?? ""} />
    </TextField>
  );
}

function LocationFieldReadOnly({ form }: { form: DailyLogForm }) {
  return (
    <form.Field name="location">
      {(field) => (
        <TextField required readOnly>
          <TextField.Label>Location</TextField.Label>
          <TextField.Input value={field.state.value} />
        </TextField>
      )}
    </form.Field>
  );
}

function LocationFieldWithProjectAddress({ form }: { form: DailyLogForm }) {
  const { projectId } = useParams({ strict: false });
  const { data } = useProjectDetailQuery(projectId!);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (!form.getFieldValue("location")) {
      form.setFieldValue("location", data.address);
    }
  }, [data, form]);

  return <LocationFieldReadOnly form={form} />;
}

function LocationFieldSkeleton() {
  return (
    <TextField required readOnly>
      <TextField.Label>Location</TextField.Label>
      <div className="w-full h-10 rounded-8 skeleton-shimmer" />
    </TextField>
  );
}

function WeatherFields({ form }: { form: DailyLogForm }) {
  return (
    <>
      <form.Field name="weather">
        {(field) => <WeatherSelector value={field.state.value} onValueChange={field.handleChange} />}
      </form.Field>

      <form.Field name="temperature">
        {(field) => (
          <TextField required>
            <TextField.Label>Temperature</TextField.Label>
            <DecimalNumberField
              value={field.state.value}
              onChange={field.handleChange}
              suffix={<span className="text-gray-900 text-body2">°F</span>}
              placeholder="72"
            />
          </TextField>
        )}
      </form.Field>

      <form.Field name="precipitation">
        {(field) => (
          <TextField>
            <TextField.Label>Precipitation</TextField.Label>
            <DecimalNumberField
              value={field.state.value}
              onChange={field.handleChange}
              suffix={<span className="text-gray-900 text-body2">mm</span>}
              placeholder="0.0"
            />
          </TextField>
        )}
      </form.Field>
    </>
  );
}

function dailyLogWeatherFieldsForForm(detail: DailyLogDetailResponse) {
  return {
    weather: detail.weather,
    temperature: Number(detail.temperature) || 0,
    precipitation: Number(detail.precipitation) || 0,
  };
}

function WeatherFieldsWithEditDailyLogDetail({ form }: { form: DailyLogForm }) {
  const { projectId, logId } = useParams({ strict: false });
  const initialized = useRef(false);

  const { data, isLoading } = useQuery({
    ...dailyLogDetailQueryOptions(projectId!, logId!),
    select: dailyLogWeatherFieldsForForm,
    enabled: Boolean(projectId && logId),
  });

  useEffect(() => {
    if (initialized.current) return;
    if (isLoading || !data) return;
    initialized.current = true;

    form.setFieldValue("weather", data.weather);
    form.setFieldValue("temperature", data.temperature);
    form.setFieldValue("precipitation", data.precipitation);
  }, [data, form, isLoading]);

  return <WeatherFields form={form} />;
}

function WeatherFieldsWithInitialData({ form }: { form: DailyLogForm }) {
  const { projectId } = useParams({ strict: false });

  const { data } = useProjectWeatherQuery(projectId!);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (!form.getFieldValue("weather")) {
      form.setFieldValue("weather", data.weather);
    }
    if (form.getFieldValue("temperature") === 0) {
      form.setFieldValue("temperature", Number(data.temperature) || 0);
    }
    if (form.getFieldValue("precipitation") === 0) {
      form.setFieldValue("precipitation", Number(data.precipitation) || 0);
    }
  }, [data, form]);

  return <WeatherFields form={form} />;
}

function WeatherFieldsSkeleton() {
  return (
    <div className="flex flex-col gap-y-6 animate-pulse">
      <TextField required>
        <TextField.Label>Weather</TextField.Label>
        <div className="w-full h-10 rounded-8 skeleton-shimmer" />
      </TextField>
      <TextField required>
        <TextField.Label>Temperature</TextField.Label>
        <div className="w-full h-10 rounded-8 skeleton-shimmer" />
      </TextField>
      <TextField>
        <TextField.Label>Precipitation</TextField.Label>
        <div className="w-full h-10 rounded-8 skeleton-shimmer" />
      </TextField>
    </div>
  );
}

function WeatherSection({ form }: { form: DailyLogForm }) {
  const { pathname } = useLocation();
  const isEditMode = pathname.includes("/edit");

  if (isEditMode) {
    return <WeatherFieldsWithEditDailyLogDetail form={form} />;
  }

  if (form.getFieldValue("weather")) {
    return <WeatherFields form={form} />;
  }

  return (
    <ErrorBoundary fallback={<WeatherFields form={form} />}>
      <Suspense fallback={<WeatherFieldsSkeleton />}>
        <WeatherFieldsWithInitialData form={form} />
      </Suspense>
    </ErrorBoundary>
  );
}

interface OverviewStepProps {
  form: DailyLogForm;
}

export function OverviewStep({ form }: OverviewStepProps) {
  const { projectId } = useParams({ strict: false });
  const sheet = useFullPageSheet();

  return (
    <>
      <div className="flex flex-col gap-y-6">
        <ErrorBoundary fallback={<LocationFieldReadOnly form={form} />}>
          <Suspense fallback={<LocationFieldSkeleton />}>
            <LocationFieldWithProjectAddress form={form} />
          </Suspense>
        </ErrorBoundary>

        <WriterField />

        <form.Field name="date">
          {(field) => (
            <DatePicker
              required
              color="secondary"
              value={field.state.value ? [field.state.value] : []}
              onValueChange={(details) => {
                const value = details.valueAsString[0];

                field.handleChange(value ? dayjs(value).format("YYYY-MM-DD") : "");
              }}
              isDateUnavailable={(date) => {
                return dayjs(`${date.toString()}T00:00:00`).isAfter(dayjs(), "day");
              }}
            >
              <DatePicker.Label>Date</DatePicker.Label>
              <DatePicker.Input />
              <DatePicker.Wrapper className="z-10">
                <DatePicker.YearView />
                <DatePicker.MonthView />
                <DatePicker.DayView />
              </DatePicker.Wrapper>
            </DatePicker>
          )}
        </form.Field>

        <WeatherSection form={form} />

        <form.Field name="tags">
          {(field) => (
            <>
              <div>
                <TextField className="mb-2">
                  <TextField.Label>Status</TextField.Label>
                </TextField>
                <div className="flex flex-wrap gap-2">
                  {field.state.value.map((tag) => (
                    <Chip key={tag.id} color="info" size="md" variant="outline" radius="round">
                      <span className="text-blue-500 text-label2">{tag.name}</span>
                    </Chip>
                  ))}
                </div>

                <Button variant="outline" color="secondary" onClick={sheet.open} className="mt-3 w-full">
                  Manage status
                </Button>
              </div>

              <OverviewStatusSheet
                projectId={projectId ?? ""}
                isOpen={sheet.isOpen}
                onClose={sheet.close}
                currentTags={field.state.value}
                onPickCatalogItem={(item) => {
                  if (field.state.value.some((t) => t.id === item.id)) return;
                  field.handleChange([...field.state.value, item]);
                }}
                onRemove={(tagId) => {
                  field.handleChange(field.state.value.filter((t) => t.id !== tagId));
                }}
              />
            </>
          )}
        </form.Field>

        <form.Field name="processRate">
          {(field) => (
            <TextField>
              <TextField.Label>Process Rate</TextField.Label>
              <IntegerNumberField
                value={field.state.value}
                onChange={field.handleChange}
                max={MAX_PROCESS_RATE}
                suffix={<Percent width={16} height={16} />}
                placeholder="10"
              />
            </TextField>
          )}
        </form.Field>
      </div>
    </>
  );
}
